import React, { useState, useEffect } from 'react';
import { 
  UserCircleIcon,
  BellIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  KeyIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon,
  LanguageIcon,
  GlobeAmericasIcon,
  GlobeEuropeAfricaIcon,
  ServerIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import ChangePasswordModal from '../components/ChangePasswordModal';
import DarkModeToggle from '../components/DarkModeToggle';
import { getSelfUsername, getSelfUserType } from '../services/userSelf';
import { useChristmasTheme } from '../components/ChristmasThemeContext';
import { useEnhancedTheme } from '../components/EnhancedThemeContext';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../services/translationService';
import { AI_ASSISTANT_UNLOCK_CLICKS, unlockAIAssistant } from '../services/aiAssistantService';

// User type definitions matching AgentTree
const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Icon mapping based on user type
const USER_TYPE_ICONS = {
  [USER_TYPES.ADMIN]: BuildingOfficeIcon,
  [USER_TYPES.COUNTRY_ADMIN]: GlobeAsiaAustraliaIcon,
  [USER_TYPES.CITY_ADMIN]: BuildingStorefrontIcon,
  [USER_TYPES.SUPER_AGENT]: StarIcon,
  [USER_TYPES.AGENT]: UserGroupIcon,
  [USER_TYPES.PLAYER]: UserIcon
};

// Color mapping based on user type
const USER_TYPE_STYLES = {
  [USER_TYPES.ADMIN]: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
  [USER_TYPES.COUNTRY_ADMIN]: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
  [USER_TYPES.CITY_ADMIN]: 'text-green-500 bg-green-50 dark:bg-green-900/20',
  [USER_TYPES.SUPER_AGENT]: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
  [USER_TYPES.AGENT]: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  [USER_TYPES.PLAYER]: 'text-gray-500 bg-gray-50 dark:bg-gray-800'
};

const SettingSection = ({ icon: Icon, title, description, children }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 py-6 last:border-0">
    <div className="flex items-start">
      <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
        <Icon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
      </div>
      <div className="ml-4 flex-1">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        <div className="mt-4">
          {children}
        </div>
      </div>
    </div>
  </div>
);

const Settings = ({ onSignOut, isModal = false, onClose }) => {
  const { t, language, setLanguage, direction } = useTranslation();
  const { isChristmasTheme, toggleChristmasTheme, isChristmasEnabled } = useChristmasTheme();
  const { isEnhancedTheme, toggleEnhancedTheme } = useEnhancedTheme();
  const [notifications, setNotifications] = React.useState(true);
  const [currency, setCurrency] = React.useState('USD');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selfUsername, setSelfUsername] = useState('');
  const [selfUserType, setSelfUserType] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [secretClickCount, setSecretClickCount] = useState(0);
  const [secretClickTimeout, setSecretClickTimeout] = useState(null);
  const navigate = useNavigate();

  // Secret profile picture click handler only for allowed usernames/user types.
  // playwinadmin: 30 presses reveals the AI assistant. Pausing for 3 seconds
  // after at least 8 presses still opens Detach Inactive Players, so that
  // shortcut stays available without cutting the 30-press sequence short.
  const handleProfilePictureClick = () => {
    const username = (selfUsername || '').toLowerCase();
    const isPlaywinAdmin = username === 'playwinadmin';
    if (username !== 'supertest' && !isPlaywinAdmin) {
      return;
    }

    if (secretClickTimeout) {
      clearTimeout(secretClickTimeout);
    }

    const newCount = secretClickCount + 1;

    if (isPlaywinAdmin && newCount >= AI_ASSISTANT_UNLOCK_CLICKS) {
      setSecretClickCount(0);
      unlockAIAssistant();
      return;
    }

    if (!isPlaywinAdmin && newCount >= 8) {
      setSecretClickCount(0);
      // The TestTransactionBetFix selector page is disabled, so route the
      // secret click straight to the Detach Inactive Players tool (the only
      // remaining option from the old selector).
      navigate('/detach-inactive-players');
      return;
    }

    setSecretClickCount(newCount);
    const timeout = setTimeout(() => {
      setSecretClickCount(0);
      if (isPlaywinAdmin && newCount >= 8) {
        navigate('/detach-inactive-players');
      }
    }, 3000);
    setSecretClickTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (secretClickTimeout) {
        clearTimeout(secretClickTimeout);
      }
    };
  }, [secretClickTimeout]);

  useEffect(() => {
    const loadSelfData = async () => {
      try {
        setIsLoading(true);
        const [username, userType] = await Promise.all([
          getSelfUsername(),
          getSelfUserType()
        ]);
        setSelfUsername(username);
        setSelfUserType(userType);
      } catch (error) {
        console.error('Error loading self data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSelfData();
  }, []);

  const handleSignOut = () => {
    if (isModal && onClose) {
      onClose(); // Close the modal first
    }
    navigate('/logout');
  };

  // Clear the locally cached user-tree data and force a hard reload so the
  // app refetches everything from the API. Useful when an end user (agent,
  // super-agent, or admin) sees stale data — e.g. a newly created child not
  // appearing yet, or a balance/currency that hasn't updated. The same keys
  // are listed in `src/data/users.js` and `src/services/userTreeService.js`.
  const handleRefreshData = () => {
    const confirmMsg = t(
      'settings.data.confirmRefresh',
      'This will clear cached data on this device and reload the page. Continue?'
    );
    if (!window.confirm(confirmMsg)) return;

    [
      'userTreeCache',
      'userTreeCacheTimestamp',
      'userTreeServiceCache',
      'userTreeServiceCacheTimestamp'
    ].forEach((k) => localStorage.removeItem(k));

    window.location.reload();
  };

  // Language options with icons instead of flags
  const languages = [
    { code: 'en', name: 'English', icon: GlobeAmericasIcon, color: 'text-blue-500' },
    { code: 'ar', name: 'العربية', icon: GlobeAsiaAustraliaIcon, color: 'text-green-500', rtl: true },
    { code: 'fr', name: 'Français', icon: GlobeEuropeAfricaIcon, color: 'text-purple-500' },
    { code: 'ku', name: 'کوردی', icon: GlobeAsiaAustraliaIcon, color: 'text-amber-500', rtl: true },
  ];

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
  };

  return (
    <div dir={direction} className={isModal ? "min-h-screen bg-gray-50 dark:bg-gray-900 pb-[calc(6rem+env(safe-area-inset-bottom))]" : "min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 pt-12 pb-24 sm:pb-12 px-4 sm:px-6 lg:px-8"}>
      <div className={isModal ? "max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8" : "max-w-3xl mx-auto"}>
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden">
          {/* Profile Section */}
          <div className="relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 h-32 bg-gradient-to-r from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700" />
            
            {/* Profile Content */}
            <div className="relative px-8 pt-8">
              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                {/* Profile Icon */}
                <div className="relative">
                  <div 
                    onClick={handleProfilePictureClick}
                    className={`w-24 h-24 rounded-xl bg-white dark:bg-gray-700 shadow-lg flex items-center justify-center cursor-pointer select-none ${USER_TYPE_STYLES[selfUserType] || USER_TYPE_STYLES[USER_TYPES.PLAYER]}`}
                  >
                    {(() => {
                      const IconComponent = USER_TYPE_ICONS[selfUserType] || UserIcon;
                      return <IconComponent className="w-14 h-14" />;
                    })()}
                  </div>
                  {selfUserType && (
                    <span className={`absolute -bottom-3 left-1/2 transform -translate-x-1/2 px-3 py-1 text-xs font-medium rounded-full shadow-md capitalize whitespace-nowrap ${
                      selfUserType === 'admin' ? 'bg-purple-500 text-white dark:bg-purple-600' :
                      selfUserType === 'country' ? 'bg-blue-500 text-white dark:bg-blue-600' :
                      selfUserType === 'city' ? 'bg-green-500 text-white dark:bg-green-600' :
                      selfUserType === 'super-agent' ? 'bg-orange-500 text-white dark:bg-orange-600' :
                      selfUserType === 'agent' ? 'bg-yellow-500 text-white dark:bg-yellow-600' :
                      'bg-gray-500 text-white dark:bg-gray-600'
                    }`}>
                      {t(`userTypes.${selfUserType}`)}
                    </span>
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {isLoading ? t('common.loading') : selfUsername || t('users.title')}
                  </h2>
                  <p className="mt-1 text-gray-600 dark:text-gray-300">
                    {t('profile.viewAndManage')}
                  </p>
                </div>

                {/* View Profile Button */}
                <button
                  onClick={() => navigate(`/users/${selfUsername}/deep-view`)}
                  className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-gray-900 dark:text-white transition-all duration-200 group"
                >
                  <span>{t('navigation.viewProfile')}</span>
                  <ChevronRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                </button>
              </div>
            </div>
          </div>

          {/* Settings Sections */}
          <div className="px-8">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {/* Security Settings */}
              <SettingSection
                icon={ShieldCheckIcon}
                title={t('settings.security.title')}
                description={t('settings.security.description')}
              >
                <div className="space-y-4">
                  <button
                    onClick={() => setShowChangePasswordModal(true)}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 dark:from-teal-500 dark:to-teal-600 dark:hover:from-teal-600 dark:hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <KeyIcon className="w-5 h-5" />
                    <span>{isLoading ? t('common.loading') : t('auth.changePassword')}</span>
                  </button>

                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-700 hover:to-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:from-gray-500 dark:to-gray-600 dark:hover:from-gray-600 dark:hover:to-gray-700 transition-all duration-200"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    <span>{t('auth.signOut')}</span>
                  </button>
                </div>
              </SettingSection>

              {/* Theme Settings */}
              <SettingSection
                icon={ComputerDesktopIcon}
                title={t('settings.theme.title')}
                description={t('settings.theme.description')}
              >
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SunIcon className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('settings.theme.light')}</span>
                  </div>
                  <DarkModeToggle />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('settings.theme.dark')}</span>
                    <MoonIcon className="w-4 h-4 text-blue-400" />
                  </div>
                </div>
                
                {/* Enhanced Theme Toggle */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-br from-blue-100 to-teal-100 dark:from-blue-900/30 dark:to-teal-900/30 rounded-lg">
                        <span className="text-xl" role="img" aria-label="Sparkles">✨</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {t('settings.theme.enhanced') || 'Enhanced Theme'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('settings.theme.enhancedDescription') || 'Elegant gradients & subtle animations'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={toggleEnhancedTheme}
                      className="enhanced-toggle"
                      aria-label="Toggle Enhanced theme"
                    >
                      <div className={`enhanced-toggle-track ${isEnhancedTheme ? 'active' : ''}`}>
                        <div className="enhanced-toggle-thumb">
                          {isEnhancedTheme ? '✨' : '◯'}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* Christmas Theme Toggle */}
                {isChristmasEnabled && (
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                          <span className="text-xl" role="img" aria-label="Christmas tree">🎄</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {t('settings.theme.christmas') || 'Christmas Theme'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('settings.theme.christmasDescription') || 'Festive snowflakes & holiday vibes'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={toggleChristmasTheme}
                        className="christmas-toggle"
                        aria-label="Toggle Christmas theme"
                      >
                        <div className={`christmas-toggle-track ${isChristmasTheme ? 'active' : ''}`}>
                          <div className="christmas-toggle-thumb">
                            {isChristmasTheme ? '❄️' : '☀️'}
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </SettingSection>

              {/* Data & Cache - available to all users */}
              <SettingSection
                icon={ArrowPathIcon}
                title={t('settings.data.title', 'Data & Cache')}
                description={t(
                  'settings.data.description',
                  'Refresh data on this device if something looks stale or out of date.'
                )}
              >
                <button
                  onClick={handleRefreshData}
                  className="mt-2 w-full flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-900 dark:text-white transition-all duration-200"
                >
                  <div className="flex items-center">
                    <ArrowPathIcon className="w-5 h-5 mr-2 text-teal-600 dark:text-teal-400" />
                    <span>{t('settings.data.refresh', 'Refresh data')}</span>
                  </div>
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </SettingSection>

              {/* Modify Domains - Restricted to playwinadmin (backend enforces too) */}
              {selfUsername === 'playwinadmin' && (
                <SettingSection
                  icon={ServerIcon}
                  title="Modify Domains"
                  description="Buy, attach, and remove custom domains for the Casino and Agent apps."
                >
                  <button
                    onClick={() => {
                      if (isModal && onClose) onClose();
                      navigate('/modify-domains');
                    }}
                    className="mt-2 w-full flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-900 dark:text-white transition-all duration-200"
                  >
                    <span>Manage domains</span>
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </SettingSection>
              )}

              {/* Language Settings - Converted to button that opens modal */}
              <SettingSection
                icon={GlobeAltIcon}
                title={t('settings.language.title')}
                description={t('settings.language.description')}
              >
                <button
                  onClick={() => setShowLanguageModal(true)}
                  className="mt-2 w-full flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-900 dark:text-white transition-all duration-200"
                >
                  <div className="flex items-center">
                    {(() => {
                      const lang = languages.find(lang => lang.code === language) || languages[0];
                      const LangIcon = lang.icon;
                      return (
                        <>
                          <div className={`p-1 rounded-full ${lang.color} bg-opacity-20 mr-3`}>
                            <LangIcon className={`w-6 h-6 ${lang.color}`} />
                          </div>
                          <span className={lang.rtl ? 'font-arabic' : ''}>
                            {lang.name}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </SettingSection>

            </div>
          </div>
        </div>
      </div>

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="language-modal" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80 transition-opacity" aria-hidden="true" onClick={() => setShowLanguageModal(false)}></div>
            
            {/* Modal panel - Made wider */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-xl mx-4 sm:mx-auto">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-teal-100 dark:bg-teal-900 sm:mx-0 sm:h-10 sm:w-10">
                    <GlobeAltIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                      {t('settings.language.selectLanguage')}
                    </h3>
                    <div className="mt-6 grid grid-cols-1 gap-4">
                      {languages.map((lang) => {
                        const LangIcon = lang.icon;
                        return (
                          <button
                            key={lang.code}
                            onClick={() => {
                              handleLanguageChange(lang.code);
                              setShowLanguageModal(false);
                            }}
                            className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 ${
                              language === lang.code
                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-400'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`p-2 rounded-full ${lang.color} bg-opacity-20 mr-3`}>
                                <LangIcon className={`w-6 h-6 ${lang.color}`} />
                              </div>
                              <span className={`text-base font-medium ${lang.rtl ? 'font-arabic' : ''}`}>
                                {lang.name}
                              </span>
                            </div>
                            {language === lang.code && (
                              <div className="w-3 h-3 rounded-full bg-teal-500 dark:bg-teal-400"></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowLanguageModal(false)}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        username={selfUsername}
        userType={selfUserType}
      />
    </div>
  );
};

export default Settings; 